import { initializeApp } from 'firebase/app';
import { getAnalytics } from "firebase/analytics";
import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  updateProfile
} from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFirestore, initializeFirestore } from 'firebase/firestore';

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
    apiKey: "AIzaSyCP-uqEgUcQVbMROHUSlUONmx1tJjC8eyA",
    authDomain: "re-stack.com", // Custom domain for Google Sign-In authorization
    projectId: "stackai-31d4e",
    storageBucket: "stackai-31d4e.firebasestorage.app", // Using the correct Firebase Storage bucket name
    messagingSenderId: "103376361923",
    appId: "1:103376361923:web:46bec71f64d759073a4576",
    measurementId: "G-5ZCJ63SJST"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app); // Initialize Analytics
const auth = getAuth(app);
const provider = new GoogleAuthProvider();
const defaultStorage = getStorage(app);

const shouldForceLongPolling = (() => {
  if (typeof window === 'undefined') {
    return false;
  }
  return ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
})();

let db;
if (shouldForceLongPolling) {
  // Force long-polling locally to avoid QUIC protocol errors when emulators + Chrome HTTP/3 clash
  db = initializeFirestore(app, {
    experimentalForceLongPolling: true,
    useFetchStreams: false,
  });
} else {
  db = getFirestore(app);
}

// Use default Firebase Storage bucket for slides (same as backend)
// This ensures compatibility between frontend Firebase SDK and backend GCS SDK
const storage = defaultStorage;
const slidesStorage = defaultStorage; // Use same bucket for simplicity

// Helpful exported identifiers for other modules
export { app };
export const FIREBASE_PROJECT_ID = app?.options?.projectId;
export const WORKSPACE_BUCKET = app?.options?.storageBucket;
export { storage, slidesStorage };
export { auth, db, analytics };

// NOTE: Even on localhost we talk to LIVE Firebase Auth and Firestore — this is a
// deliberate choice so local dev tests against real prod data and real OAuth
// (only the Functions run in the emulator, with TEST Stripe keys). We only tweak
// transport (long polling) for stable local connections. Heads-up: because the
// Functions emulator's admin.firestore() also binds to LIVE Firestore in this
// setup, local billing actions write to production data — start.sh prints a
// reminder, and subscription.js logs one at boot (see ALLOW_LIVE_FIRESTORE).

// Configure Google Auth Provider
provider.addScope('profile');
provider.addScope('email');

// Set persistence to LOCAL (survives browser refresh)
setPersistence(auth, browserLocalPersistence)
    .catch((error) => {
        console.error("Error setting auth persistence:", error);
    });

// Sign in with Google
export const signInWithGoogle = async () => {
    try {
        console.log('Starting Google sign-in...');

        // First, check if there's a pending redirect result
        const redirectResult = await getRedirectResult(auth);
        if (redirectResult) {
            console.log('Google sign-in successful via redirect:', redirectResult.user);
            return redirectResult.user;
        }

        // Try popup first
        try {
            const result = await signInWithPopup(auth, provider);
            console.log('Google sign-in successful via popup:', result.user);
            return result.user;
        } catch (popupError) {
            console.log('Popup failed, trying redirect:', popupError.code);

            // If popup fails due to blocking or CSP issues, use redirect
            if (popupError.code === 'auth/popup-blocked' ||
                popupError.code === 'auth/popup-closed-by-user' ||
                popupError.code === 'auth/cancelled-popup-request' ||
                popupError.code === 'auth/internal-error') {

                console.log('Falling back to redirect method...');
                await signInWithRedirect(auth, provider);

                // The redirect will handle the sign-in, and the result will be
                // picked up by getRedirectResult on the next page load
                return null; // Indicates redirect is in progress
            } else {
                throw popupError; // Re-throw other errors
            }
        }
    } catch (error) {
        console.error('Error signing in with Google:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);

        // Provide more specific error messages
        let errorMessage = 'Failed to sign in. Please try again.';

        switch (error.code) {
            case 'auth/popup-blocked':
                errorMessage = 'Popup blocked. Redirecting to Google sign-in...';
                break;
            case 'auth/popup-closed-by-user':
                errorMessage = 'Sign-in was cancelled. Please try again.';
                break;
            case 'auth/unauthorized-domain':
                errorMessage = 'This domain is not authorized for sign-in. Please contact support.';
                break;
            case 'auth/cancelled-popup-request':
                errorMessage = 'Sign-in was cancelled due to another request. Please try again.';
                break;
            case 'auth/network-request-failed':
                errorMessage = 'Network error. Please check your connection and try again.';
                break;
            default:
                errorMessage = error.message || 'An unexpected error occurred. Please try again.';
        }

        const customError = new Error(errorMessage);
        customError.code = error.code;
        throw customError;
    }
};

// Sign in with Microsoft (Azure AD work/school + personal accounts).
// Restack is a PowerPoint plugin — Microsoft 365 is the dominant identity
// provider for our enterprise customers, so MS sign-in needs to be a
// first-class option alongside Google.
//
// Tenant=`common` accepts both work/school accounts AND personal Microsoft
// accounts (e.g. @outlook.com). For per-tenant SSO restrictions later,
// switch to a specific tenant ID.
export const signInWithMicrosoft = async () => {
    try {
        console.log('Starting Microsoft sign-in...');
        const provider = new OAuthProvider('microsoft.com');
        provider.setCustomParameters({ tenant: 'common', prompt: 'select_account' });
        provider.addScope('openid');
        provider.addScope('email');
        provider.addScope('profile');

        const result = await signInWithPopup(auth, provider);
        console.log('Microsoft sign-in successful:', result.user.email);
        return result;
    } catch (error) {
        console.error('Microsoft sign-in error:', error.code, error.message);

        // The email is already registered with a DIFFERENT sign-in method, so
        // Firebase ("one account per email") blocks the Microsoft credential.
        // This is a SIGN-IN, not account creation — tell the user exactly which
        // method their account uses so they can get straight in, rather than the
        // old vague "use your original method" copy that read like a dead end.
        if (error.code === 'auth/account-exists-with-different-credential') {
            // The email belongs to an account created in the PowerPoint add-in
            // via Office SSO — keyed by a custom uid (microsoft_{oid}) with NO
            // federated microsoft.com provider, so this browser popup can't match
            // it directly. Recover by taking the Microsoft id_token from this
            // popup and exchanging it server-side for a custom token bound to the
            // SAME account (the same mechanism the add-in uses). End result: the
            // website signs into the identical account, no password needed.
            try {
                const pendingCred = OAuthProvider.credentialFromError(error);
                const msIdToken = pendingCred?.idToken;
                if (msIdToken) {
                    const resp = await fetch('/api/auth/microsoft-web', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ idToken: msIdToken }),
                    });
                    if (resp.ok) {
                        const data = await resp.json();
                        if (data?.customToken) {
                            const result = await signInWithCustomToken(auth, data.customToken);
                            console.log('Microsoft sign-in via web exchange:', result.user.email);
                            return result;
                        }
                    } else {
                        const body = await resp.text().catch(() => '');
                        console.warn('microsoft-web exchange failed:', resp.status, body);
                    }
                } else {
                    console.warn('microsoft-web exchange: no id_token on pending credential');
                }
            } catch (exchErr) {
                console.error('microsoft-web exchange error:', exchErr);
            }
            // Exchange didn't complete — surface a clear, actionable fallback.
            const fallbackErr = new Error('We could not finish Microsoft sign-in. Please try again, or use "Forgot password?" to sign in with email, or contact hi@re-stack.com.');
            fallbackErr.code = error.code;
            throw fallbackErr;
        }

        let errorMessage = 'Failed to sign in with Microsoft. Please try again.';
        switch (error.code) {
            case 'auth/popup-blocked':
                errorMessage = 'Popup blocked. Please allow popups for this site and try again.';
                break;
            case 'auth/popup-closed-by-user':
                errorMessage = 'Sign-in was cancelled. Please try again.';
                break;
            case 'auth/unauthorized-domain':
                errorMessage = 'This domain is not authorized for sign-in. Please contact support.';
                break;
            default:
                errorMessage = error.message || errorMessage;
        }
        const customError = new Error(errorMessage);
        customError.code = error.code;
        throw customError;
    }
};

// Sign in with email and password
export const signInWithEmail = async (email, password) => {
    try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        console.log('Email sign-in successful:', result.user.email);
        return result.user;
    } catch (error) {
        console.error('Email sign-in error:', error.code);
        let errorMessage = 'Sign-in failed. Please try again.';
        switch (error.code) {
            case 'auth/invalid-credential':
            case 'auth/wrong-password':
            case 'auth/user-not-found':
                errorMessage = 'Invalid email or password.';
                break;
            case 'auth/invalid-email':
                errorMessage = 'Please enter a valid email address.';
                break;
            case 'auth/user-disabled':
                errorMessage = 'This account has been disabled.';
                break;
            case 'auth/too-many-requests':
                errorMessage = 'Too many attempts. Please try again later.';
                break;
            case 'auth/operation-not-allowed':
                errorMessage = 'Email sign-in is not enabled. Please use Google sign-in or contact support.';
                break;
            default:
                errorMessage = error.message || errorMessage;
        }
        const customError = new Error(errorMessage);
        customError.code = error.code;
        throw customError;
    }
};

// Create account with email and password
export const signUpWithEmail = async (email, password, displayName) => {
    try {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        if (displayName) {
            await updateProfile(result.user, { displayName });
        }
        // Email verification removed — users are signed in immediately on
        // signup. (Product is Microsoft-first; the SMTP verification path
        // was unreliable and gated access unnecessarily.)
        console.log('Email sign-up successful:', result.user.email);
        return result.user;
    } catch (error) {
        console.error('Email sign-up error:', error.code);
        let errorMessage = 'Account creation failed. Please try again.';
        switch (error.code) {
            case 'auth/email-already-in-use':
                errorMessage = 'An account with this email already exists. Try signing in instead.';
                break;
            case 'auth/invalid-email':
                errorMessage = 'Please enter a valid email address.';
                break;
            case 'auth/weak-password':
                errorMessage = 'Password must be at least 6 characters.';
                break;
            default:
                errorMessage = error.message || errorMessage;
        }
        const customError = new Error(errorMessage);
        customError.code = error.code;
        throw customError;
    }
};

// Resend verification email — routes through our requestEmailVerification
// Cloud Function so the link is delivered from hi@re-stack.com via the
// nodemailer/Gmail SMTP chain (same as welcome / password-reset / pod-invite
// emails), not from Firebase's default noreply sender which has historically
// landed in spam.
export const resendVerificationEmail = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    if (user.emailVerified) throw new Error('Email already verified');

    const projectId = app?.options?.projectId || 'stackai-31d4e';
    const url = `https://us-central1-${projectId}.cloudfunctions.net/requestEmailVerification`;
    const idToken = await user.getIdToken();
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
        }
    });
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to send verification email.');
    }
    console.log('Verification email sent to:', user.email);
};

// Send password reset email
// Routes through our own requestPasswordReset Cloud Function so the email
// goes out from hi@re-stack.com via nodemailer/Gmail (same chain as welcome
// emails) instead of Firebase's default noreply sender, which was landing
// in spam and breaking the "click the link" flow.
//
// Always hits the deployed function — even on localhost — because the
// Functions emulator has its own isolated Firebase Auth user pool, so a
// reset there wouldn't reach the real user. This mirrors the existing
// convention in this file of talking to live Firebase Auth/Firestore
// even during local dev.
export const resetPassword = async (email) => {
    try {
        const projectId = app?.options?.projectId || 'stackai-31d4e';
        const url = `https://us-central1-${projectId}.cloudfunctions.net/requestPasswordReset`;
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to send reset email.');
        }
        console.log('Password reset email sent to:', email);
    } catch (error) {
        console.error('Password reset error:', error.message);
        const errorMessage = error.message || 'Failed to send reset email.';
        const customError = new Error(errorMessage);
        throw customError;
    }
};

// Sign out
export const signOutUser = async () => {
    try {
        await signOut(auth);
    } catch (error) {
        console.error('Error signing out:', error);
        throw error;
    }
};

// Auth state listener
export const onAuthStateChange = (callback) => {
    return onAuthStateChanged(auth, callback);
};

export default app;
